import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Box,
    useTheme,
    TextField,
    InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink } from 'react-router-dom';

const RedditTrackerTable = ({ data, title }) => {
    const theme = useTheme();
    const [searchTerm, setSearchTerm] = useState('');

    const filteredData = data.filter(stock =>
        stock.symbol?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stock.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(price);
    };

    const formatMarketCap = (marketCap) => {
        if (!marketCap) return 'N/A';

        if (marketCap >= 1e12) {
            return `$${(marketCap / 1e12).toFixed(2)}T`;
        } else if (marketCap >= 1e9) {
            return `$${(marketCap / 1e9).toFixed(2)}B`;
        } else if (marketCap >= 1e6) {
            return `$${(marketCap / 1e6).toFixed(2)}M`;
        }
        return formatPrice(marketCap);
    };

    const formatPercentage = (value) => {
        if (value === null || value === undefined) return 'N/A';
        const formatted = value.toFixed(2);
        return value >= 0 ? `+${formatted}%` : `${formatted}%`;
    };

    const getPercentageColor = (value) => {
        if (!value) return theme.palette.text.secondary;
        return value >= 0 ? theme.palette.positive.main : theme.palette.negative.main;
    };

    const getSentimentColor = (sentiment) => {
        switch (sentiment) {
            case 'Bullish':
                return 'success';
            case 'Bearish':
                return 'error';
            case 'Neutral':
            default:
                return 'default';
        }
    };

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {title}
                    </Typography>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: { xs: '100%', sm: 250 } }}
                    />
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Rank</strong></TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Symbol</strong></TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><strong>Weight %</strong></TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><strong>Market Cap</strong></TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><strong>Price</strong></TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><strong>% Change</strong></TableCell>
                                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}><strong>Sentiment</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((stock) => (
                                <TableRow
                                    key={stock.symbol}
                                    hover
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.05)'
                                                : 'rgba(0, 0, 0, 0.02)'
                                        }
                                    }}
                                >
                                    <TableCell>{stock.rank}</TableCell>
                                    <TableCell>
                                        <Box>
                                            <RouterLink
                                                to={`/asset/${stock.symbol}`}
                                                style={{
                                                    color: theme.palette.primary.main,
                                                    textDecoration: 'none',
                                                    fontSize: '1rem',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {stock.symbol}
                                            </RouterLink>
                                            <Box
                                                sx={{
                                                    fontSize: '0.875rem',
                                                    color: theme.palette.text.secondary,
                                                    fontWeight: 500,
                                                    mt: 0.5
                                                }}
                                            >
                                                {stock.name || 'N/A'}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                            {stock.weightPercentage ? `${stock.weightPercentage.toFixed(2)}%` : 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatMarketCap(stock.marketCap)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatPrice(stock.price)}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ color: getPercentageColor(stock.changesPercentage) }}
                                    >
                                        <strong>{formatPercentage(stock.changesPercentage)}</strong>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={stock.sentiment}
                                            color={getSentimentColor(stock.sentiment)}
                                            size="small"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {filteredData.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                            No stocks found matching "{searchTerm}"
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default RedditTrackerTable;